import { useState } from 'react'

import { formatMoney } from '../formatting/money'
import type { StickerComponent } from '../stickers/types'
import type { BaseAdjustmentComponent } from './BaseAdjustment'
import { formatMoneyDelta } from './formatters'
import { getMoneyStep } from './moneyStep'
import {
  MONEY_CEILING,
  MONEY_FLOOR,
  type MoneyAdjustmentSlot,
} from './useAdjustments'
import useHoldAcceleration, { MONEY_TIERS } from './useHoldAcceleration'

export type MoneyAdjustmentProps = {
  BaseAdjustment: BaseAdjustmentComponent
  slot: MoneyAdjustmentSlot
  Sticker: StickerComponent
  label: string
  onInteractionEnd?: () => void
}

const noop = (): void => {}

/**
 * A `BaseAdjustment` specialized for money values. Each tick calls
 * `slot.step`, so the slot's `ratio` mutates immediately and the chart
 * pipeline re-renders in lockstep with the displayed value. `onPress` is a
 * no-op to avoid a double-fire with the press-start tick.
 *
 * Platform-agnostic — pass the platform's `BaseAdjustment`.
 */
export const MoneyAdjustment = ({
  BaseAdjustment,
  slot,
  Sticker,
  label,
  onInteractionEnd,
}: MoneyAdjustmentProps) => {
  const moneyValue = Math.round(slot.baseline * (1 + slot.ratio))
  // A slot may narrow the range to its own record; What-If leaves it to the
  // global one.
  const floor = slot.min ?? MONEY_FLOOR
  const ceiling = slot.max ?? MONEY_CEILING

  const formatValue = (m: number): string =>
    formatMoney(m, moneyValue < 1000 ? 'exact' : 'precise')

  // One drag, one geometry.
  //
  // `displayStep` drives the ruler strip, which paints `value ± n × step`. Taking
  // the step from the live value meant crossing a bracket boundary mid-drag
  // relabelled the whole strip with new spacing, so the numbers appeared to jump
  // sideways under the finger. Latched on the first step of a gesture, released
  // when the finger lifts.
  //
  // The `?? liveStep` fallback is what makes a burst work. The ruler multiplies
  // drag velocity into a step COUNT and fires `onStep` that many times in one
  // synchronous loop, so `latchedStep` is still null on the second call — but
  // `liveStep` is a constant of that render, so every tick of the burst moves by
  // the same amount either way.
  const liveStep = getMoneyStep(moneyValue)
  const [latchedStep, setLatchedStep] = useState<number | null>(null)
  const step = latchedStep ?? liveStep

  const handleStep = (direction: number): void => {
    if (latchedStep === null) setLatchedStep(liveStep)
    slot.step(direction * step)
  }

  const handleInteractionEnd = (): void => {
    setLatchedStep(null)
    onInteractionEnd?.()
  }

  const inc = useHoldAcceleration({
    tiers: MONEY_TIERS,
    onTick: step => slot.step(step),
  })
  const dec = useHoldAcceleration({
    tiers: MONEY_TIERS,
    onTick: step => slot.step(-step),
  })

  return (
    <BaseAdjustment
      Sticker={Sticker}
      label={label}
      value={moneyValue}
      baseline={slot.baseline}
      formatValue={formatValue}
      formatDelta={(diff, baseline) => formatMoneyDelta(diff, baseline)}
      betterWhen={slot.betterWhen}
      onReset={slot.isModified ? slot.reset : undefined}
      onIncrement={noop}
      onDecrement={noop}
      onIncrementPressStart={inc.start}
      onIncrementPressEnd={() => {
        inc.stop()
        handleInteractionEnd()
      }}
      onDecrementPressStart={dec.start}
      onDecrementPressEnd={() => {
        dec.stop()
        handleInteractionEnd()
      }}
      onStep={handleStep}
      onInteractionEnd={handleInteractionEnd}
      isIncrementDisabled={moneyValue >= ceiling}
      isDecrementDisabled={moneyValue <= floor}
      displayStep={step}
      displayMin={floor}
      displayMax={ceiling}
    />
  )
}
